import { type FC, useId, useState, useEffect } from "react";
import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { CheckedState } from "@radix-ui/react-checkbox";
import { Checkbox } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CheckboxGroup.module.scss";

type CheckboxLabelProps = {
  /** DOM id of the checkbox control itself (e.g. for a label's `htmlFor`). */
  id: string;
  /**
   * Id to put on the label element so the control can name itself via
   * `aria-labelledby`. Custom label components should place it on their text.
   */
  labelId?: string;
} & PlatformComponentProps;

export type ChildCheckboxComponentProps = {
  id: string;
  /** Id for the label element, referenced by the control's `aria-labelledby`. */
  labelId?: string;
  checked: boolean;
  onCheckedChange: (checkedState: CheckedState) => void;
  label?: JSX.Element | string;
  labelComponent?: React.FC<CheckboxLabelProps>;
  disabled?: boolean;
};

type CheckboxGroupItemClassNames = Partial<{
  itemContainer: string;
  container?: string;
}>;

export interface CheckoxGroupComponentProps extends PlatformComponentProps {
  options: {
    value: string;
    label?: JSX.Element | string;
    labelComponent?: React.FC<CheckboxLabelProps>;
  }[];
  checkedOptions: string[];
  onOptionClick?: (val: string) => void;
  onOptionsChange?: (val: string[]) => void;
  checkboxElement?: FC<ChildCheckboxComponentProps>;
  classNames?: CheckboxGroupItemClassNames;
  disabled?: boolean;
}

const DefaultLabel = ({ id, labelId, children }: CheckboxLabelProps) => {
  return (
    <label id={labelId} htmlFor={id}>
      {children}
    </label>
  );
};

const DefaultCheckbox = ({
  id,
  labelId,
  checked,
  onCheckedChange,
  label,
  labelComponent,
  disabled,
}: ChildCheckboxComponentProps) => {
  const Label = labelComponent ?? DefaultLabel;
  // The <Checkbox> renders a bare <button role="checkbox"> with the label as a
  // sibling; a <label htmlFor> does not name a button. Point the control at the
  // label element via aria-labelledby so screen readers announce the option.
  return (
    <>
      <Checkbox
        disabled={disabled}
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-labelledby={labelId}
      />
      <Label id={id} labelId={labelId}>
        {label}
      </Label>
    </>
  );
};

export const CheckboxGroup = (props: CheckoxGroupComponentProps) => {
  const COMPONENT_KEY = CoreUiComponents.CheckboxGroup;
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const [checkedOptionInternal, setCheckedOptions] = useState<string[]>(
    props.checkedOptions ?? []
  );

  useEffect(() => {
    setCheckedOptions(props.checkedOptions ?? []);
  }, [props.checkedOptions]);

  const handleCheckboxChange = (value: string, checked: boolean) => {
    const updatedCheckedOptions = checked
      ? [...checkedOptionInternal, value]
      : checkedOptionInternal.filter((option) => option !== value);

    setCheckedOptions(updatedCheckedOptions);
    props.onOptionsChange?.(updatedCheckedOptions);
  };

  const CheckboxComponent = props.checkboxElement || DefaultCheckbox;

  const id = useId();

  return (
    <ul className={clsx(block, props.className, props.classNames?.container)}>
      {props.options?.map((option) => {
        const isChecked = checkedOptionInternal.includes(option.value);
        return (
          <li
            key={option.value}
            className={clsx(cn("option"), props.classNames?.itemContainer)}
          >
            <CheckboxComponent
              id={`${id}_${option.value}`}
              labelId={`${id}_${option.value}-label`}
              checked={isChecked}
              onCheckedChange={(checkState) => {
                handleCheckboxChange(option.value, !!checkState);
                props.onOptionClick?.(option.value);
              }}
              label={option.label}
              labelComponent={option.labelComponent}
              disabled={props.disabled}
            />
          </li>
        );
      })}
    </ul>
  );
};
